import {
  PROFILE_BANNER_SIZE_CLASSES,
  ProfileBanner,
  type ProfileBannerClassNames,
  type ProfileBannerProps,
} from '@repo/shared-client/components/blocks'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { useAuth } from '@web/src/hooks/use-auth'
import { useTranslation } from '@web/src/lib/intl/react'
import { BadgeCheckIcon } from 'lucide-react'
import { type ComponentProps, forwardRef } from 'react'
import { useSFContext } from 'src/contexts/sf-context'
import { onlineNow } from 'src/lib/utilsClient'
import type { NSFWRatingEnum, ReviewEnum } from 'types/graphql'
import { shouldBlurContent } from '../sf-media-image/sf-media-image'
import SFSupporterSymbol from '../sf-supporter-symbol/sf-supporter-symbol'

type ProfileBannerSize = NonNullable<ProfileBannerProps['size']>

export type SFProfileBannerClassNames = ProfileBannerClassNames & {
  badge?: string
  supporter?: string
  supporterImage?: string
}

interface SFProfileBannerProps extends Omit<ProfileBannerProps, 'classNames'> {
  profile: {
    id?: string
    name?: string
    isVerified?: boolean
    lastOnline?: string | Date
    supporter?: boolean
    profileMedia?: {
      reviewStatus?: ReviewEnum
      imageUrlSmall?: string
      nsfwRating?: NSFWRatingEnum | string
    }
  }

  classNames?: SFProfileBannerClassNames
  size?: ProfileBannerSize
  lowerTitle?: React.ReactNode
  rightTitle?: React.ReactNode
  avatarItem?: React.ReactNode
  disableLinks?: boolean
  hideProfileName?: boolean
  hideOnlineStatus?: boolean
  hideSupporterStatus?: boolean
  disableNSFWBlur?: boolean
}

const SF_PROFILE_BANNER_BADGE_SIZE_CLASSES: Record<
  ProfileBannerSize,
  { badge: string; supporter: string; supporterImage: string }
> = {
  xs: {
    badge: 'size-4',
    supporter: '-top-1 -left-1.5 size-3.5',
    supporterImage: 'size-[14px]',
  },
  sm: {
    badge: 'size-4',
    supporter: '-top-1 -left-1.5 size-5',
    supporterImage: 'size-[20px]',
  },
  md: {
    badge: 'size-4',
    supporter: '-top-1 -left-1.5 size-5',
    supporterImage: 'size-[20px]',
  },
  lg: {
    badge: 'size-5',
    supporter: '-top-0.75 -left-1.25 size-5',
    supporterImage: 'size-[20px]',
  },
  xl: {
    badge: 'size-5',
    supporter: '-top-0.75 -left-1.25 size-6',
    supporterImage: 'size-[24px]',
  },
  '2xl': {
    badge: 'size-5',
    supporter: '-top-0.75 -left-1.25 size-6',
    supporterImage: 'size-[24px]',
  },
  '3xl': {
    badge: 'size-5',
    supporter: '-top-0.75 -left-1.25 size-6',
    supporterImage: 'size-[24px]',
  },
  '4xl': {
    badge: 'size-5',
    supporter: '-top-0.75 -left-1.25 size-6',
    supporterImage: 'size-[24px]',
  },
}

export const SF_PROFILE_BANNER_SIZE_CLASSES = {
  xs: { ...PROFILE_BANNER_SIZE_CLASSES.xs, ...SF_PROFILE_BANNER_BADGE_SIZE_CLASSES.xs },
  sm: { ...PROFILE_BANNER_SIZE_CLASSES.sm, ...SF_PROFILE_BANNER_BADGE_SIZE_CLASSES.sm },
  md: { ...PROFILE_BANNER_SIZE_CLASSES.md, ...SF_PROFILE_BANNER_BADGE_SIZE_CLASSES.md },
  lg: { ...PROFILE_BANNER_SIZE_CLASSES.lg, ...SF_PROFILE_BANNER_BADGE_SIZE_CLASSES.lg },
  xl: { ...PROFILE_BANNER_SIZE_CLASSES.xl, ...SF_PROFILE_BANNER_BADGE_SIZE_CLASSES.xl },
  '2xl': { ...PROFILE_BANNER_SIZE_CLASSES['2xl'], ...SF_PROFILE_BANNER_BADGE_SIZE_CLASSES['2xl'] },
  '3xl': { ...PROFILE_BANNER_SIZE_CLASSES['3xl'], ...SF_PROFILE_BANNER_BADGE_SIZE_CLASSES['3xl'] },
  '4xl': { ...PROFILE_BANNER_SIZE_CLASSES['4xl'], ...SF_PROFILE_BANNER_BADGE_SIZE_CLASSES['4xl'] },
} as const

export const SFProfileBanner = forwardRef<HTMLDivElement, SFProfileBannerProps>((props, forwardedRef) => {
  const {
    profile,
    lowerTitle,
    rightTitle,
    avatarItem,
    size = 'md',
    classNames,
    disableLinks = false,
    hideProfileName = false,
    hideOnlineStatus = true,
    hideSupporterStatus = false,
    disableNSFWBlur = false,
    params: _paramsFromParent,
    to: _toFromParent,
    ...rest
  } = props

  const { currentUser, isAuthenticated } = useAuth()
  const { isNSFWEnabled } = useSFContext()
  const { t } = useTranslation()

  const sizeClasses = SF_PROFILE_BANNER_SIZE_CLASSES[size]

  const isOnline = !hideOnlineStatus && onlineNow(profile?.lastOnline)

  // `shouldBlurContent` treats a missing rating and `UNDEFINED` (the media default) as
  // explicit. Avatars are often unrated, so only blur when a real rating is present.
  const mediaNsfwRating = profile?.profileMedia?.nsfwRating as NSFWRatingEnum | undefined
  const hasClassifiedRating = Boolean(mediaNsfwRating && mediaNsfwRating !== 'UNDEFINED')

  const shouldBlur =
    !disableNSFWBlur &&
    isNSFWEnabled &&
    hasClassifiedRating &&
    Boolean(profile?.profileMedia?.imageUrlSmall) &&
    profile?.profileMedia?.imageUrlSmall !== '/missing-profile.webp' &&
    shouldBlurContent(currentUser?.profile?.settingNSFW as NSFWRatingEnum, mediaNsfwRating)

  return (
    <ProfileBanner
      avatarChild={
        <>
          {/* Supporter Symbol */}
          {!hideSupporterStatus && profile?.supporter && (
            <SFSupporterSymbol
              className={cn('absolute z-3', sizeClasses.supporter, classNames?.supporter)}
              size={classNames?.supporterImage ?? sizeClasses.supporterImage}
            />
          )}
          {!!avatarItem && avatarItem}
          {props.avatarChild}
        </>
      }
      blurImage={shouldBlur}
      classNames={{
        avatar: classNames?.avatar,
        meta: classNames?.meta,
        name: classNames?.name,
      }}
      isOnline={isOnline}
      leftUpperChild={
        <>
          {profile?.isVerified && !hideProfileName && (
            <Tooltip>
              <TooltipTrigger
                className="-mx-1.5 size-[25px]"
                render={
                  <div className="inline-flex size-[25px] shrink-0 items-center justify-center">
                    <BadgeCheckIcon aria-hidden className={cn('text-blue-400', sizeClasses.badge, classNames?.badge)} />
                  </div>
                }
              />
              <TooltipContent>{t('Verified Profile')}</TooltipContent>
            </Tooltip>
          )}
        </>
      }
      lowerChild={lowerTitle}
      missingProfileImageUrl="/missing-profile.webp"
      params={disableLinks ? undefined : ({ profileId: profile?.id } as unknown as ProfileBannerProps['params'])}
      profileImageUrl={profile?.profileMedia?.imageUrlSmall}
      profileName={hideProfileName ? undefined : profile?.name || 'Unknown User'}
      ref={forwardedRef}
      rightChild={props.rightChild}
      rightUpperChild={rightTitle}
      size={size}
      to={disableLinks ? undefined : isAuthenticated ? '/profile/$profileId/about' : '/auth/signup'}
      {...(rest as ComponentProps<typeof ProfileBanner>)}
    />
  )
})

export default SFProfileBanner
